<template>
    <section-wrapper
        :class="Section.INVESTMENT_GOALS.cssClassName"
        :this-section="Section.INVESTMENT_GOALS"
        :next-section="Section.INVESTMENT_STRATEGY"
        :section-end-button-label="$t('investmentGoals.button.label')"
        :section-end-button-is-disabled="!isInvestmentValid">
        <div class="row">
            <div class="col">
                <h2>{{ $t('investmentGoals.title') }}</h2>
                <div class="investment-goals__items d-lg-flex">
                    <radio-item
                        :checked="investmentObjective === InvestmentObjective.ASSET_GENERATION"
                        icon="moneyBundle"
                        :title="$t('investmentGoals.assetGeneration.title')"
                        :text="$t('investmentGoals.assetGeneration.text')"
                        @click="handleClick(InvestmentObjective.ASSET_GENERATION)" />

                    <radio-item
                        :checked="investmentObjective === InvestmentObjective.RETIREMENT"
                        icon="pillar"
                        :title="$t('investmentGoals.retirement.title')"
                        :text="$t('investmentGoals.retirement.text')"
                        @click="handleClick(InvestmentObjective.RETIREMENT)" />
                    <transition name="fade-xs">
                        <div v-if="investmentObjective === InvestmentObjective.RETIREMENT && showAlert">
                            <b-alert
                                class="d-lg-none alert--embed"
                                variant="success"
                                dismissible
                                v-model="showAlert">
                                {{ $t('investmentGoals.retirement.alert') }}
                            </b-alert>
                        </div>
                    </transition>

                    <radio-item
                        :checked="investmentObjective === InvestmentObjective.REAL_ESTATE"
                        icon="immo"
                        :title="$t('investmentGoals.realEstate.title')"
                        :text="$t('investmentGoals.realEstate.text')"
                        @click="handleClick(InvestmentObjective.REAL_ESTATE)" />
                    <transition name="fade-xs">
                        <div v-if="investmentObjective === InvestmentObjective.REAL_ESTATE && showAlert">
                            <b-alert
                                class="d-lg-none alert--embed"
                                variant="success"
                                dismissible
                                v-model="showAlert">
                                {{ $t('investmentGoals.realEstate.alert') }}
                            </b-alert>
                        </div>
                    </transition>

                    <radio-item
                        :checked="investmentObjective === InvestmentObjective.EXPENSES"
                        icon="education"
                        :title="$t('investmentGoals.expenses.title')"
                        :text="$t('investmentGoals.expenses.text')"
                        @click="handleClick(InvestmentObjective.EXPENSES)" />
                    <transition name="fade-xs">
                        <div v-if="investmentObjective === InvestmentObjective.EXPENSES && showAlert">
                            <b-alert
                                class="d-lg-none alert--embed"
                                variant="success"
                                dismissible
                                v-model="showAlert">
                                {{ $t('investmentGoals.expenses.alert') }}
                            </b-alert>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
        <div class="row d-none d-lg-block">
            <div class="col">
                <transition name="fade">
                    <div v-if="showAlert">
                        <b-alert
                            v-if="investmentObjective === InvestmentObjective.RETIREMENT"
                            variant="success"
                            dismissible
                            v-model="showAlert">
                            {{ $t('investmentGoals.retirement.alert') }}
                        </b-alert>
                        <b-alert
                            v-if="investmentObjective === InvestmentObjective.REAL_ESTATE"
                            variant="success"
                            dismissible
                            v-model="showAlert">
                            {{ $t('investmentGoals.realEstate.alert') }}
                        </b-alert>
                        <b-alert
                            v-if="investmentObjective === InvestmentObjective.EXPENSES"
                            variant="success"
                            dismissible
                            v-model="showAlert">
                            {{ $t('investmentGoals.expenses.alert') }}
                        </b-alert>
                    </div>
                </transition>
            </div>
        </div>
        <div class="row investment-goals__slider-section-wrapper">
            <div class="col d-lg-none">
                <h3>{{ $t('investmentGoals.combinedSmallScreenTitle') }}</h3>
            </div>
            <div class="col-lg-6">
                <h3 class="d-none d-lg-block">
                    {{ $t('investmentGoals.investmentHorizon.title') }}
                </h3>
                <investment-goals-horizon
                    @onInvestmentHorizonChange="onInvestmentHorizonChange"
                    @onInvestmentHorizonChangeConfirmed="onInvestmentHorizonChangeConfirmed"
                    :investment-horizon="investmentHorizon" />
            </div>
            <div class="col-lg-6">
                <h3 class="d-none d-lg-block">
                    {{ $t('investmentGoals.investmentAmount.title') }}
                </h3>
                <investment-goals-amount
                    @onInvestmentAmountChange="onInvestmentAmountChange"
                    :investment-amount="investmentAmount" />
            </div>
        </div>
    </section-wrapper>
</template>

<script>
import { InvestmentObjective, InvestmentStrategyType } from '@/api/enums';
import RadioItem from '@/components/common/RadioItem';
import InvestmentGoalsHorizon from '@/components/InvestmentGoalsHorizon';
import InvestmentGoalsAmount from '@/components/InvestmentGoalsAmount';
import investmentStrategyTypeApi from '@/api/investmentStrategyTypeApi';
import { Section } from '@/enums';
import SectionWrapper from '@/components/common/SectionWrapper';

import { mapState, mapActions, mapGetters } from 'vuex';

export default {
    name: 'InvestmentGoals',
    computed: {
        ...mapState(['investmentObjective', 'investmentAmount', 'investmentHorizon', 'settings']),
        ...mapGetters(['isInEditModeWithoutValueChanges']),
        isInvestmentValid () {
            return this.investmentObjective && this.investmentAmount && this.investmentHorizon;
        },
    },
    components: {
        RadioItem,
        InvestmentGoalsHorizon,
        InvestmentGoalsAmount,
        SectionWrapper,
    },
    data () {
        return {
            InvestmentObjective,
            Section,
            showAlert: false,
        };
    },
    methods: {
        ...mapActions(['setInvestmentObjective', 'setInvestmentHorizon', 'setInvestmentAmount',
            'setInvestmentStrategyRecommendedBasedOnInvestmentHorizon', 'setInvestmentStrategySelected']),
        handleClick (newObjective) {
            this.setInvestmentObjective(newObjective);
        },
        onInvestmentHorizonChange (horizon) {
            this.setInvestmentHorizon(horizon);
        },
        onInvestmentHorizonChangeConfirmed (horizon) {
            this.onInvestmentHorizonChange(horizon);
            investmentStrategyTypeApi.fetchStrategy(horizon).then(investmentStrategyType => {
                this.setInvestmentStrategyRecommendedBasedOnInvestmentHorizon(InvestmentStrategyType[investmentStrategyType]);
                this.setInvestmentStrategySelected(InvestmentStrategyType[investmentStrategyType]);
            });
        },
        onInvestmentAmountChange (amount) {
            this.setInvestmentAmount(amount);
        },
    },
    watch: {
        investmentObjective (val) {
            if (val !== InvestmentObjective.ASSET_GENERATION) {
                this.showAlert = true;
            } else {
                this.showAlert = false;
            }
        },
        isInEditModeWithoutValueChanges (val) {
            if (val) {
                this.showAlert = false;
            } else {
                this.showAlert = true;
            }
        },
    },
    created () {
        this.setInvestmentObjective(InvestmentObjective.ASSET_GENERATION);
    },
};
</script>

<style scoped lang="scss">
  .investment-goals {
    padding-top: 20px;
    padding-bottom: 15px;
  }

  .alert {
      line-height: 22px;
  }

  .investment-goals__slider-section-wrapper {
    margin-top: 20px;

    @include media-breakpoint-down(xs) {
      margin-top: 0;
    }

    h3 {
        color: $primary;
    }

  }

  .investment-goals__items {
    margin-bottom: 20px;

    @include media-breakpoint-down(xs) {
      margin-bottom: 10px;

        .alert {
            line-height: 16px;
            padding-top: 15px;
            padding-bottom: 15px;
        }
    }
  }

  .investment_goals__button {
  @include media-breakpoint-down(xs) {
    margin-top: 5px;
  }
}
</style>
